import _ from "lodash";
/* eslint-disable react/display-name */
import React from "react";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";

import { getCriteriaSetDetailsFromCache } from "modules/CommonGraphqlHelpers";
import { colors } from "Constants/colorConstants";
import I18nHOC from "UIComponents/I18nHOC";
import { Trans } from "react-i18next";
import { getRatingHistoryDate } from "AppComponents/ProgressSummaryV2Shared/modules/Utils.js";
import classNames from "classnames";
import {
  getTitle,
  getAssociatedIconInfo,
} from "ClassProgressSummary/modules/Utils";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import classes from "./HistoryGraph.scss";
import Thumbnail from "../Thumbnail";

const styles = {
  container: {
    marginBottom: "8px",
  },
  bodyLabel: {
    fontSize: "1.4rem",
  },
  itemsContainer: {
    background: colors.white,
  },
  thumbnailSize: {
    width: "28px",
    height: "28px",
  },
  ratingText: {
    fontSize: "1.0rem",
  },
  tooltipContainer: {
    padding: "8px",
    width: "200px",
  },
  publishTime: {
    fontSize: "1.0rem",
  },
  tooltipInnerContainer: {
    justifyContent: "space-between",
  },
};

const RATING_HISTORY_LIMIT = 30;

const HistoryGraph = React.memo(props => {
  const {
    academicCriteriaValues,
    criteriaType,
    ratingHistory,
    t,
    isPopUp,
  } = props;

  const numOfCriteriaValues = _.size(academicCriteriaValues);

  const MAX_RATING_CHARACTERS_LIMIT = 4;

  const subTitleClass = classNames({
    [classes.subtitle]: true,
    [classes.graySubtitle]: isPopUp,
  });
  /**
   * This adds a property academicCriteriaIndex which is an index
   * of the criteria in academicCriteriaValues. It helps to determine
   * the priority of these values.  academicCriteriaValues has values
   * order according to the priority. 0 - high priority, last - low priority
   */
  const parseRatingHistoryForGraph = () => {
    const tempRatingHistory = _.map(ratingHistory, rating => {
      const academicCriteriaIndex = _.findIndex(
        academicCriteriaValues,
        value => value.id === rating.academicCriteriaValue
      );
      return {
        ...rating,
        academicCriteriaIndex,
      };
    });

    // Remove all non-existing/invalid rating history
    _.remove(tempRatingHistory, rating => rating.academicCriteriaIndex < 0);

    return tempRatingHistory.length > RATING_HISTORY_LIMIT
      ? tempRatingHistory.slice(0, RATING_HISTORY_LIMIT)
      : tempRatingHistory;
  };

  /**
   * This function is to map the input tick item to a tickItem
   * which is going to be rendered on the axis.
   *
   * @param {String} tickItem - The index on the Y-axis
   * @returns {String} abbreviation - This returns a value of the
   * corresponding tickItem
   */
  const tickFormatter = tickItem => {
    const { abbreviation } = academicCriteriaValues[tickItem];
    if (_.size(abbreviation) <= MAX_RATING_CHARACTERS_LIMIT)
      return abbreviation;
    return `${abbreviation.substring(0, MAX_RATING_CHARACTERS_LIMIT)}...`;
  };

  const isRatingCharacterLimitExceeded = _.find(
    academicCriteriaValues,
    value => _.size(value?.abbreviation) >= MAX_RATING_CHARACTERS_LIMIT
  );

  /**
   * This function is to render the tooltip content based on
   * certain conditions.
   *
   * @param {Object} param0 - payload : This has the all data of a
   * point on the graph.
   *
   * @returns {JSX} - Returns the content in the form of JSX,
   *
   */
  const tooltipContent = ({ payload }) => {
    const { t } = props;

    if (payload) {
      const [firstValue] = payload;

      if (firstValue) {
        // This payload has the coordinate values. This is different
        // from the parent payload.
        const {
          payload: { academicCriteriaValue, publishedAt, parent, parentType },
        } = firstValue;

        const { iconAssessmentTypeLabel } = getAssociatedIconInfo({
          parentType,
          parent,
          t,
        });

        // Get the abbreviation for the matching academic criteria value
        const { abbreviation, label, color } = _.find(
          academicCriteriaValues,
          value => value.id == academicCriteriaValue
        );
        const showAcademicCriteriaValueLabel = criteriaType !== "IB_DEFINED";

        return (
          <div
            className={classes.tooltipContainer}
            style={isPopUp && styles.tooltipContainer}
          >
            <div
              className={classNames(
                classes.tooltipInnerContainer,
                classes.topInnerContainer
              )}
              style={isPopUp && styles.tooltipInnerContainer}
            >
              <div
                className={classes.ratingText}
                style={isPopUp && styles.ratingText}
              >
                <Trans i18nKey={!isPopUp && "common:evaluated_with_label"}>
                  {!isPopUp && "Evaluated:"}
                  <span style={{ color: color }}>
                    {{ label: abbreviation }}
                  </span>
                </Trans>
                {showAcademicCriteriaValueLabel && <span>{`(${label})`}</span>}
              </div>
              <div
                className={classes.publishTime}
                style={isPopUp && styles.publishTime}
              >
                {getRatingHistoryDate({ publishedAt, t })}
              </div>
            </div>

            <div className={classes.tooltipInnerContainer}>
              <Thumbnail
                parent={parent}
                displayOn={"graph"}
                parentType={parentType}
              />

              <div className={classes.assessmentContent}>
                <div className={classes.title}>
                  {getTitle({ parent, parentType })}
                </div>
                <div className={subTitleClass}>{iconAssessmentTypeLabel}</div>
              </div>
            </div>
          </div>
        );
      }
    }
    return null;
  };
  return (
    <div className={classes.container} style={isPopUp && styles.container}>
      <div className={classes.bodyLabel}>
        {!isPopUp && t("progressSummary:grading_history")}
      </div>
      <div
        className={classes.itemsContainer}
        style={isPopUp && styles.itemsContainer}
      >
        <ResponsiveContainer height={isPopUp ? 162 : 200}>
          <LineChart
            margin={
              !isPopUp
                ? { right: 22, top: 26, bottom: 26, left: -12 }
                : { right: 20, top: 16, bottom: 20, left: -20 }
            }
            data={parseRatingHistoryForGraph()}
          >
            <CartesianGrid
              vertical={false}
              stroke={"#ebebeb"}
              fill={"transparent"}
            />
            <XAxis hide={true} reversed={true} />
            <YAxis
              type={"number"}
              tick={{
                fill: colors.grey48,
                fontSize: isPopUp ? "8px" : "10px",
              }}
              tickLine={false}
              axisLine={false}
              reversed={criteriaType != "IB_DEFINED"}
              interval={0}
              tickCount={numOfCriteriaValues}
              domain={[0, numOfCriteriaValues - 1]}
              tickFormatter={tickFormatter}
              width={isRatingCharacterLimitExceeded ? 68 : 60}
            />
            <Tooltip content={tooltipContent} />
            <Line
              dataKey={"academicCriteriaIndex"}
              stroke={"rgba(247, 89, 97, 0.5)"}
              type={"linear"}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});

const mapStateToProps = (state, ownProps) => {
  const {
    academicCriteriaValues,
    criteriaType,
  } = getCriteriaSetDetailsFromCache(ownProps.academicCriteriaSetId);
  return {
    academicCriteriaValues,
    criteriaType,
  };
};

export default compose(
  connect(mapStateToProps, null),
  I18nHOC({ resource: ["common", "progressSummary"] })
)(HistoryGraph);
